const DIAGNOSTICS_ENABLED =
  process.env.FAST_REFRESH === false &&
  process.env.REACT_APP_RUNTIME_DIAGNOSTICS === "true";

function toDiagnosticTime(value) {
  if (!value) return null;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().toISOString();
  if (typeof value?.seconds === "number") {
    return value.seconds * 1000 + Number(value.nanoseconds || 0) / 1e6;
  }
  return value;
}

export function describeRuntimeSessionRecord(record = null) {
  if (!record) return null;
  const continuation = record?.postSessionContinuation || {};
  return {
    id: record.id ?? null,
    idType: typeof record.id,
    orgId: String(record.orgId || ""),
    sessionComplete: record.sessionComplete === true,
    endedAt: toDiagnosticTime(record.endedAt),
    updatedAt: toDiagnosticTime(record.updatedAt),
    messageCount: Number(
      record.messageCount || (Array.isArray(record.messages) ? record.messages.length : 0)
    ),
    completionVersionId: String(record?.completionVersion?.id || ""),
    completionFingerprint: String(
      record?.completionVersion?.transcriptFingerprint || ""
    ),
    summary: record.sessionSummary
      ? {
          generationStatus: String(record.sessionSummary.generationStatus || ""),
          status: String(record.sessionSummary.status || ""),
          canonical: record.sessionSummary.canonical === true,
          isCurrent: record.sessionSummary.isCurrent === true,
          summaryVersion: String(record.sessionSummary.summaryVersion || ""),
          completionVersionId: String(
            record.sessionSummary.completionVersionId || ""
          ),
          sourceTranscriptFingerprint: String(
            record.sessionSummary.sourceTranscriptFingerprint || ""
          ),
        }
      : null,
    continuation: {
      finalizationStatus: String(continuation?.finalization?.status || ""),
      finalizationSummaryVersion: String(
        continuation?.finalization?.summaryVersion || ""
      ),
      automaticEmailStatus: String(continuation?.automaticEmail?.status || ""),
      automaticRelayStatus: String(
        continuation?.automaticEmail?.relayStatus || ""
      ),
      reminderStatus: String(continuation?.reminder?.status || ""),
    },
  };
}

export function recordRuntimeDiagnostic(type, detail = {}) {
  if (!DIAGNOSTICS_ENABLED || typeof window === "undefined") return;
  const store = window.__NIA_RUNTIME_DIAGNOSTICS__ || {
    sequence: 0,
    events: [],
  };
  store.sequence += 1;
  store.events.push({
    sequence: store.sequence,
    type,
    at: new Date().toISOString(),
    ...detail,
  });
  window.__NIA_RUNTIME_DIAGNOSTICS__ = store;
}

export async function runRuntimeTestHook(name, detail = {}) {
  if (!DIAGNOSTICS_ENABLED || typeof window === "undefined") return;
  const hook = window.__NIA_RUNTIME_TEST_HOOKS__?.[name];
  if (typeof hook !== "function") return;
  recordRuntimeDiagnostic("runtime-test-hook-start", { name, ...detail });
  const result = await hook(detail);
  recordRuntimeDiagnostic("runtime-test-hook-finish", { name, ...detail });
  return result;
}

export function readRuntimeTestConfig(name) {
  if (
    !DIAGNOSTICS_ENABLED ||
    typeof window === "undefined" ||
    !name
  ) {
    return undefined;
  }
  return window.__NIA_RUNTIME_TEST_CONFIG__?.[name];
}

export function registerRuntimeTestAction(name, handler) {
  if (
    !DIAGNOSTICS_ENABLED ||
    typeof window === "undefined" ||
    !name ||
    typeof handler !== "function"
  ) {
    return () => {};
  }
  const actions = window.__NIA_RUNTIME_TEST_ACTIONS__ || {};
  actions[name] = handler;
  window.__NIA_RUNTIME_TEST_ACTIONS__ = actions;
  return () => {
    if (window.__NIA_RUNTIME_TEST_ACTIONS__?.[name] === handler) {
      delete window.__NIA_RUNTIME_TEST_ACTIONS__[name];
    }
  };
}
